import React from 'react';
import { useLanguage } from '../i18n';

/**
 * The connection status line under the conversation title.
 *
 * The dot carries a matching glow — a small light source rather than a flat
 * chip — and the label takes the dot's colour, so the state reads at a glance
 * without a second visual device.
 *
 * ⚠️ The label MUST come from the app's own dictionary and carry
 * translate="no" (user-testing hotfix 2026-10-06). It used to be hard-coded
 * English inside an otherwise Spanish UI, which left it as the one string on
 * the page a browser translator would still rewrite — a tester saw a GREEN dot
 * (state 'connected', confirmed by prod logs: every /health poll from that
 * browser answered 200 in a few ms) labelled "Desconectado". The dot and the
 * label are now derived from the same entry, and nothing outside the app may
 * rewrite the label.
 */
const STATES = {
    connected: { color: '#34D058', key: 'statusConnected', text: 'Connected' },
    loading: { color: '#F5A524', key: 'statusLoading', text: 'Loading model...' },
    error: { color: '#F04438', key: 'statusError', text: 'Connection error' },
    // Before the first probe answers (and while a probe is merely slow — a
    // Cloud Run cold start takes ~8s) we do not KNOW we are disconnected, so we
    // must not say so.
    checking: { color: 'rgba(255,255,255,0.45)', key: 'statusChecking', text: 'Connecting...' },
};
const DEFAULT_STATE = STATES.checking;

const StatusIndicator = ({ status }) => {
    const { t } = useLanguage();
    const { color, key, text } = STATES[status] || DEFAULT_STATE;
    return (
        <div
            className="flex items-center gap-2 mt-1 text-sm font-semibold"
            style={{ color }}
            data-testid="api-status"
            data-status={STATES[status] ? status : 'checking'}
            translate="no"
        >
            <span
                className="w-2 h-2 rounded-full flex-none"
                style={{ background: color, boxShadow: `0 0 9px ${color}` }}
            />
            {t(key, text)}
        </div>
    );
};

export default StatusIndicator;
